import type { ReactNode } from 'react';
import { NumeroFita } from '@/pages/gerenciar/components/TilePainel';
import type { ResumoFinanceiro } from './tipos';
import { conta, plural, reais } from './rotulos';
import Info from './Info';

function CartaoNumero({
  titulo,
  valor,
  valorCls = 'text-foreground-950',
  base,
  aoClicar,
  className = '',
  children,
}: {
  titulo: string;
  valor: string;
  valorCls?: string;
  base?: string;
  aoClicar?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const classe = `flex min-h-[116px] w-full min-w-0 flex-col rounded-3xl border border-secondary-100 bg-white p-4 text-left ${
    className
  } ${aoClicar ? 'cursor-pointer transition-colors hover:border-primary-300' : ''}`;
  const corpo = (
    <>
      <span className="text-[13px] font-semibold text-foreground-500">{titulo}</span>
      <NumeroFita valor={valor} base={base} className={`mt-2 min-w-0 ${valorCls}`} />
      <span className="mt-2 block text-[12px] leading-snug text-foreground-500">{children}</span>
    </>
  );
  if (aoClicar) {
    return (
      <button type="button" onClick={aoClicar} className={classe}>
        {corpo}
      </button>
    );
  }
  return <div className={classe}>{corpo}</div>;
}

export default function BlocoResumo({
  resumo,
  aoIrParaEstornos,
}: {
  resumo: ResumoFinanceiro;
  aoIrParaEstornos: () => void;
}) {
  const soTeste = resumo.estornos_sem_testes === 0 && Number(resumo.estornos ?? 0) > 0;
  const valorEstornado = soTeste ? 'R$ 0,00' : reais(resumo.estornado_sem_testes);
  const linhaEstornado = soTeste
    ? `${conta(resumo.estornos)} de teste`
    : `${conta(resumo.estornos_sem_testes)} ${plural(
        resumo.estornos_sem_testes,
        'lançamento',
        'lançamentos',
      )}`;

  return (
    <div className="grid grid-cols-2 gap-3">
      <CartaoNumero
        titulo="Líquido para vocês"
        valor={reais(resumo.liquido)}
        valorCls="text-primary-600"
        base="text-[clamp(26px,10vw,40px)] md:text-[clamp(28px,2.4vw,38px)]"
        className="col-span-2"
      >
        é o que fica para o evento
      </CartaoNumero>

      <CartaoNumero
        titulo="Vendido"
        valor={reais(resumo.vendido)}
        base="text-[clamp(17px,5.4vw,26px)] md:text-[clamp(20px,1.8vw,28px)]"
      >
        {conta(resumo.lancamentos)} {plural(resumo.lancamentos, 'pagamento', 'pagamentos')} ·{' '}
        {conta(resumo.itens)} {plural(resumo.itens, 'item', 'itens')}
      </CartaoNumero>

      <CartaoNumero
        titulo="Taxas"
        valor={reais(resumo.taxas)}
        base="text-[clamp(17px,5.4vw,26px)] md:text-[clamp(20px,1.8vw,28px)]"
      >
        <span className="block">
          Se Liga {reais(resumo.taxa_seliga)} · Mercado Pago{' '}
          {resumo.mp_estimado ? '≈ ' : ''}
          {reais(resumo.taxa_mp)}
          {resumo.mp_estimado && (
            <>
              {' '}
              <Info texto="Tarifa do Mercado Pago estimada em 0,99% por Pix. A tarifa exata de cada venda entra em breve." />
            </>
          )}
        </span>
        {resumo.taxa_efetiva_pct != null && (
          <span className="mt-0.5 block text-foreground-400">
            {Number(resumo.taxa_efetiva_pct).toLocaleString('pt-BR', {
              maximumFractionDigits: 1,
            })}
            % do vendido
          </span>
        )}
      </CartaoNumero>

      <CartaoNumero
        titulo="Estornado"
        valor={valorEstornado}
        valorCls="text-warning-700"
        base="text-[clamp(17px,5.4vw,26px)] md:text-[clamp(20px,1.8vw,28px)]"
        aoClicar={aoIrParaEstornos}
      >
        {linhaEstornado}
      </CartaoNumero>
    </div>
  );
}